import React from 'react';
import { CheckCircle, X, Plus, Sparkles, ArrowRight } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const Pricing = () => {
  // Define all modules with their details
  const allModules = {
    coreHR: {
      name: "Core HR Management",
      icon: "Users",
      features: ["Employee Database", "Organization Chart", "Self-Service Portal", "Document Management"]
    },
    leaveAttendance: {
      name: "Leave & Attendance",
      icon: "Calendar",
      features: ["Leave Management", "Attendance Tracking", "Shift Scheduling", "Timesheet Management"]
    },
    payroll: {
      name: "Payroll Management",
      icon: "DollarSign",
      features: ["Salary Processing", "Tax Compliance", "Payslip Generation", "Bank Integration"]
    },
    communication: {
      name: "Communication & Collaboration",
      icon: "MessageSquare",
      features: ["Instant Messaging", "Notice Board", "Announcements", "File Sharing"]
    },
    reports: {
      name: "Reports & Analytics",
      icon: "BarChart3",
      features: ["Custom Reports", "Real-Time Dashboards", "Data Export", "HR Metrics"]
    }
  };

  // Define pricing plans
  const pricingPlans = [
    {
      id: 1,
      name: "Starter",
      description: "Perfect for small teams getting started with HR management",
      badge: null,
      modules: [
        { ...allModules.coreHR, included: true },
        { ...allModules.leaveAttendance, included: true },
        { ...allModules.payroll, included: false },
        { ...allModules.communication, included: false },
        { ...allModules.reports, included: false }
      ],
      highlighted: false
    },
    {
      id: 2,
      name: "Professional",
      description: "Ideal for growing businesses with comprehensive HR needs",
      badge: "Most Popular",
      modules: [
        { ...allModules.coreHR, included: true },
        { ...allModules.leaveAttendance, included: true },
        { ...allModules.payroll, included: true },
        { ...allModules.communication, included: true },
        { ...allModules.reports, included: false }
      ],
      highlighted: true
    },
    {
      id: 3,
      name: "Enterprise",
      description: "Complete HR solution for large organizations",
      badge: "Full Suite",
      modules: [
        { ...allModules.coreHR, included: true },
        { ...allModules.leaveAttendance, included: true },
        { ...allModules.payroll, included: true },
        { ...allModules.communication, included: true },
        { ...allModules.reports, included: true }
      ],
      highlighted: false
    }
  ];

  // Define add-on modules
  const addonModules = [
    {
      id: 1,
      name: "Recruitment & Onboarding",
      description: "Streamline hiring from start to finish",
      icon: "UserPlus",
      features: [
        "Job Posting Management",
        "Applicant Tracking System (ATS)",
        "Resume Parsing",
        "Interview Scheduling",
        "Onboarding Workflows",
        "Digital Document Collection"
      ]
    },
    {
      id: 2,
      name: "Performance Management",
      description: "Drive employee growth and success",
      icon: "TrendingUp",
      features: [
        "Goal Setting & Tracking (OKRs/KPIs)",
        "Performance Reviews & Appraisals",
        "360-Degree Feedback",
        "Skills Assessment",
        "Development Plans",
        "Performance Analytics"
      ]
    },
    {
      id: 3,
      name: "In-App Wallet System",
      description: "Digital expense and payment management",
      icon: "Wallet",
      features: [
        "Employee Wallet Accounts",
        "Instant Fund Transfers",
        "Expense Request & Approval",
        "Real-Time Spend Tracking",
        "Transaction History",
        "Expense Reports"
      ]
    },
    {
      id: 4,
      name: "Policy & Compliance",
      description: "Manage policies and ensure compliance",
      icon: "FileCheck",
      features: [
        "Policy Creation & Distribution",
        "Employee Acknowledgments",
        "Version Control",
        "Compliance Tracking",
        "Audit Trails",
        "Digital Signatures"
      ]
    }
  ];

  // All modules with full details for the grid
  const allModulesDetailed = [
    {
      id: 1,
      name: "Core HR Management",
      description: "Essential HR tools for modern teams",
      icon: "Users",
      features: [
        "Employee Database Management",
        "Organization Chart",
        "Employee Self-Service Portal",
        "Document Management",
        "Role-Based Access Control",
        "Custom Fields & Forms"
      ]
    },
    {
      id: 2,
      name: "Leave & Attendance",
      description: "Smart time and attendance tracking",
      icon: "Calendar",
      features: [
        "Leave Request & Approval Workflows",
        "Multiple Leave Types",
        "Leave Balance Tracking",
        "Attendance Tracking",
        "Biometric Integration",
        "Geofencing & GPS Tracking",
        "Timesheet Management",
        "Shift Scheduling"
      ]
    },
    {
      id: 3,
      name: "Payroll Management",
      description: "Automated payroll processing",
      icon: "DollarSign",
      features: [
        "Automated Salary Calculations",
        "Multi-Currency Support",
        "Tax Compliance & Deductions",
        "Payslip Generation",
        "Bank Integration",
        "Bonus & Incentive Management",
        "Payroll Reports & Analytics"
      ]
    },
    {
      id: 4,
      name: "Recruitment & Onboarding",
      description: "Streamline hiring from start to finish",
      icon: "UserPlus",
      features: [
        "Job Posting Management",
        "Applicant Tracking System (ATS)",
        "Resume Parsing",
        "Interview Scheduling",
        "Candidate Communication",
        "Onboarding Workflows",
        "Digital Document Collection",
        "New Hire Portal"
      ]
    },
    {
      id: 5,
      name: "Performance Management",
      description: "Drive employee growth and success",
      icon: "TrendingUp",
      features: [
        "Goal Setting & Tracking (OKRs/KPIs)",
        "Performance Reviews & Appraisals",
        "360-Degree Feedback",
        "Continuous Performance Tracking",
        "Skills Assessment",
        "Development Plans",
        "Performance Analytics"
      ]
    },
    {
      id: 6,
      name: "In-App Messaging",
      description: "Internal communication hub",
      icon: "MessageCircle",
      features: [
        "Direct Messaging",
        "Group Conversations",
        "File Attachments",
        "Message Search",
        "Read Receipts",
        "Push Notifications",
        "Mobile Access"
      ]
    },
    {
      id: 7,
      name: "Communication & Collaboration",
      description: "Keep your team connected",
      icon: "MessageSquare",
      features: [
        "Instant Messaging",
        "Team Channels",
        "Internal Notice Board",
        "Company Announcements",
        "File Sharing",
        "Read Receipts & Notifications",
        "Mobile App Access"
      ]
    },
    {
      id: 8,
      name: "Policy & Compliance",
      description: "Manage policies and ensure compliance",
      icon: "FileCheck",
      features: [
        "Policy Creation & Distribution",
        "Employee Acknowledgments",
        "Version Control",
        "Compliance Tracking",
        "Audit Trails",
        "Digital Signatures",
        "Regulatory Compliance Reports"
      ]
    },
    {
      id: 9,
      name: "Reports & Analytics",
      description: "Data-driven HR insights",
      icon: "BarChart3",
      features: [
        "Pre-Built HR Reports",
        "Custom Report Builder",
        "Real-Time Dashboards",
        "Data Visualization",
        "Export to Excel/PDF",
        "Scheduled Reports",
        "HR Metrics & KPIs"
      ]
    }
  ];

  const getIcon = (iconName, className = "w-6 h-6") => {
    const Icon = LucideIcons[iconName];
    return Icon ? <Icon className={className} /> : null;
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-b from-gray-900 to-gray-800">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, #0066FF 1px, transparent 1px),
                             radial-gradient(circle at 75% 75%, #0066FF 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}></div>
        </div>
        <div className="absolute top-10 right-10 w-32 md:w-64 h-32 md:h-64 bg-[#0066FF]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-40 md:w-80 h-40 md:h-80 bg-[#0052CC]/20 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <Badge className="bg-white/10 text-white hover:bg-white/20 border-0 mb-4">
            Pricing
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white">
            Choose the{' '}
            <span className="bg-gradient-to-r from-[#0066FF] to-[#60a5fa] bg-clip-text text-transparent">
              Plan
            </span>{' '}
            <span className="text-white">That Fits</span>
          </h1>
          <p className="text-base md:text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Flexible pricing plans designed to scale with your business. Start with what you need and add more as you grow.
          </p>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Pricing Plans
            </h2>
            <p className="text-gray-600">Select a plan that matches your organization's needs</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {pricingPlans.map((plan) => (
              <Card
                key={plan.id}
                className={`relative border-2 transition-all duration-300 ${
                  plan.highlighted
                    ? 'border-[#0066FF] shadow-2xl scale-105'
                    : 'border-gray-100 hover:border-[#0066FF]/50 hover:shadow-lg'
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge className="bg-[#0066FF] text-white px-4 py-1">
                      <Sparkles className="w-3 h-3 mr-1" />
                      {plan.badge}
                    </Badge>
                  </div>
                )}
                <CardHeader className="text-center pt-8">
                  <CardTitle className="text-2xl font-bold mb-2">{plan.name}</CardTitle>
                  <CardDescription className="text-sm">{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide text-center md:text-left">
                      Modules Included
                    </p>
                    <ul className="space-y-3">
                      {plan.modules.map((module, index) => (
                        <li key={index} className="flex items-center justify-center md:justify-start space-x-3">
                          {module.included ? (
                            <CheckCircle className="w-5 h-5 text-[#0066FF] flex-shrink-0" />
                          ) : (
                            <X className="w-5 h-5 text-gray-300 flex-shrink-0" />
                          )}
                          <span className={`text-sm ${module.included ? 'text-gray-700' : 'text-gray-400'}`}>
                            {module.name}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Button
                    className={`w-full mt-8 ${
                      plan.highlighted
                        ? 'bg-[#0066FF] hover:bg-[#0052CC] text-white'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                    }`}
                    onClick={() => window.location.href = '/contact'}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Add-on Modules */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-[#0066FF]/10 text-[#0066FF] hover:bg-[#0066FF]/20 border-0 mb-4">
              <Plus className="w-3 h-3 mr-1" />
              Add-ons
            </Badge>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Extend Your Platform
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Enhance your HR capabilities with these powerful add-on modules. Available with any plan.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {addonModules.map((module) => (
              <Card
                key={module.id}
                className="border-2 border-gray-100 hover:border-[#0066FF] hover:shadow-xl transition-all duration-300 group"
              >
                <CardHeader>
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#0066FF] to-[#0052CC] rounded-xl flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 transition-transform duration-200">
                      {getIcon(module.icon, "w-7 h-7")}
                    </div>
                    <div>
                      <CardTitle className="text-xl mb-1">{module.name}</CardTitle>
                      <CardDescription className="text-sm">{module.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2">
                    {module.features.map((feature, index) => (
                      <div key={index} className="flex items-start justify-center md:justify-start space-x-2">
                        <CheckCircle className="w-4 h-4 text-[#0066FF] flex-shrink-0 mt-0.5" />
                        <span className="text-xs text-gray-600 text-left">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* All Modules Detail Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-[#0066FF]/10 text-[#0066FF] hover:bg-[#0066FF]/20 border-0 mb-4">
              Complete Overview
            </Badge>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Explore All Modules in Detail
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              A comprehensive look at every module and feature available on the HRMQore platform.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allModulesDetailed.map((module) => (
              <Card
                key={module.id}
                className="border-2 border-gray-100 hover:border-[#0066FF] hover:shadow-xl transition-all duration-300 group"
              >
                <CardHeader className="text-center md:text-left">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#0066FF] to-[#0052CC] rounded-xl flex items-center justify-center text-white mb-4 group-hover:scale-110 transition-transform duration-200 mx-auto md:mx-0">
                    {getIcon(module.icon, "w-7 h-7")}
                  </div>
                  <CardTitle className="text-xl mb-1">{module.name}</CardTitle>
                  <CardDescription className="text-sm">{module.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {module.features.map((feature, index) => (
                      <li key={index} className="flex items-start justify-center md:justify-start space-x-2">
                        <CheckCircle className="w-4 h-4 text-[#0066FF] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-gray-600 text-left">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-12 md:p-16 text-white relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#0066FF]/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#0066FF]/10 rounded-full blur-2xl"></div>
            
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Need a{' '}
                <span className="bg-gradient-to-r from-[#0066FF] to-[#60a5fa] bg-clip-text text-transparent">
                  Custom Solution?
                </span>
              </h2>
              <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
                Every business is unique. Let&apos;s discuss your specific requirements and create a tailored package that works perfectly for your organization.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-[#0066FF] to-[#0052CC] text-white hover:opacity-90 px-8"
                  onClick={() => window.location.href = '/contact'}
                >
                  Contact Sales
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-gray-600 text-white hover:bg-white/10"
                  onClick={() => window.location.href = '/customer-stories'}
                >
                  See Customer Stories
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
